'use client';

import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '@/lib/api-base';

export type ApiStatus = 'checking' | 'online' | 'offline';

// Use /api/v1/config as the connectivity probe — it's a public endpoint
// (no auth required) and routes through the Next.js /api/* rewrite in local dev.
// The bare /health endpoint doesn't go through the rewrite.
const HEALTH_URL = `${API_BASE}/api/v1/config`;
const PING_TIMEOUT_MS = 5_000;
const POLL_INTERVAL_MS = 5_000;

async function pingHealth(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
  try {
    const resp = await fetch(HEALTH_URL, { signal: controller.signal });
    return resp.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Monitors API server connectivity via /health pings.
 *
 * Returns the current status and a setter to simulate offline mode
 * (also triggered by `?simulateOffline=1` in the URL).
 */
export function useApiStatus(): { status: ApiStatus; setSimulateOffline: (v: boolean) => void } {
  const [status, setStatus] = useState<ApiStatus>('checking');
  const [simulateOffline, setSimulateOffline] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Pick up URL param on mount (use window.location to avoid useSearchParams SSR issues)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('simulateOffline') === '1') {
        setSimulateOffline(true);
      }
    }
  }, []);

  useEffect(() => {
    if (simulateOffline) {
      setStatus('offline');
      return;
    }

    let cancelled = false;

    const check = async () => {
      const ok = await pingHealth();
      if (cancelled) return;
      const next = ok ? 'online' : 'offline';
      setStatus(next);
      // Stop polling once online; re-check is triggered on visibility change
      if (next === 'online' && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Start polling if offline and not already polling
      if (next !== 'online' && !intervalRef.current) {
        intervalRef.current = setInterval(check, POLL_INTERVAL_MS);
      }
    };

    // Initial check
    check();

    // Re-check when tab becomes visible (catches API recovery while tab was hidden)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [simulateOffline]);

  return { status, setSimulateOffline };
}
